import * as React from 'react';
import { useEffect } from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import {
  LocalizationProvider,
  loadMessages,
  IntlProvider,
} from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../../AlertsGrid/GridLocale';
import { Checkbox, Divider, FormControlLabel } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import esMessages from '../../../utils/gridMessageKey.json';
loadMessages(esMessages, 'fr-CA');
import StringResourceManager, { GetString } from '../../StringResourceManager';
import { ReferenceWeightsFilePath } from '../../../Interfaces/ReferenceWeightsFilePath';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridCustomFilterCellProps,
} from '@progress/kendo-react-grid';
import {
  commandStatusIconMap,
  getDateTimeLocaleString,
  getLocalDateTime,
  useChipStyle,
} from '../../../utils/utils';
import { DataResult, process, State } from '@progress/kendo-data-query';
import {
  setExpandedState,
  setGroupIds,
} from '@progress/kendo-react-data-tools';
import { GroupedCommandRecord } from '../../../Interfaces/Commands/CommandRecordInfo';
import {
  getCommandByDateRange,
  resetCommandFetchError,
  resetNotificationMessage,
  updateCommandById,
} from '../../../store/Commands/CommandsAction';
import { Button } from '@progress/kendo-react-buttons';
import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import { Error } from '@progress/kendo-react-labels';
import { arrowRotateCwIcon } from '@progress/kendo-svg-icons';
import { LoadingPanel } from '../../LoadingPanel';
import { commandStatusTextMap } from '../../../utils/textMappings';
import { Chip } from '@progress/kendo-react-buttons';
import { FetchError } from '../../../Interfaces/FetchError';
import { CustomDateFilterCell } from '../../CustomDateFilterCell';
import { ApiEnums } from '../../../Interfaces/ApiEnums';
import { AccessControlHistoryStatusFilterCell } from './AccessControlHistoryStatusFilterCell';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    send: {
      fontSize: '1.5rem',
    },
    span: {
      fontWeight: 'bold',
      fontSize: '1rem',
      margin: '10px',
    },
    commandHistoryContainer: {
      marginLeft: '10px',
      marginBottom: '20px',
    },
    margin: {
      margin: theme.spacing(1),
    },
    gridStyle: {
      height: '65vh',
      marginLeft: '10px',
      marginBottom: '10px',
      maxHeight: '90vh',
    },
    startDateRangeContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      width: 500,
      marginTop: '10px',
    },
    endDateRangeContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      width: 500,
      marginTop: '10px',
    },
    labelContainer: {
      width: 100,
    },
    dateTimePickerContainer: {
      display: 'flex',
      flexDirection: 'row',
    },
    divider: { border: 'solid 1px' },
    button: { marginLeft: '10px' },
    startDateTimePickerContainer: { width: 320 },
    endDateTimePickerContainer: { width: 550 },
  }),
);

const AccessControlHistoryContainer: React.FC = (): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const status = GetString('STATUS');

  const actionButton = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    if (dataItem.Status === ApiEnums.CommandStatus.New)
      return (
        <td style={{ textAlign: 'center' }}>
          <Button
            onClick={() => {
              updateCommandById(
                dispatch,
                dataItem.ID,
                ApiEnums.CommandStatus.Completed,
              );
            }}
          >
            {GetString('CLOSE_BUTTON')}
          </Button>
        </td>
      );
    else return <td></td>;
  };

  const CreatedOnCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    return <td>{getLocalDateTime(dataItem.CreatedOn)}</td>;
  };

  const CloseOnCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;

    if (dataItem.ExecutionNotifiedOn)
      return <td>{getDateTimeLocaleString(dataItem.ExecutionNotifiedOn)}</td>;
    else return <td></td>;
  };

  const CreatedOnFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="CreatedOn"
      />
    );
  };

  const ExecutionNotifiedOnFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="ExecutionNotifiedOn"
      />
    );
  };

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const commandList: GroupedCommandRecord[] = useSelector(
    (state: ApplicationState) => state.commands.commandListByDateRange,
  );

  const loading: boolean = useSelector(
    (state: ApplicationState) => state.commands.isLoadingCommands,
  );

  const message: string = useSelector(
    (state: ApplicationState) => state.commands.notificationMessage,
  );

  const fetchError: FetchError = useSelector(
    (state: ApplicationState) => state.commands.fetchError,
  );

  const initialDataState: State = {
    take: 50,
    skip: 0,
    sort: [{ field: 'CreatedOn', dir: 'desc' }],
    group: [],
  };

  const processWithGroups = (data: any, dataState: State): DataResult => {
    const newDataState = process(data, dataState);

    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };

  const [dataState, setDataState] = React.useState<State>(initialDataState);
  const [collapsedState, setCollapsedState] = React.useState<string[]>([]);

  const [resultState, setResultState] = React.useState<DataResult>(
    processWithGroups(commandList, dataState),
  );

  const newData = setExpandedState({
    data: resultState.data,
    collapsedIds: collapsedState,
  });

  useEffect(() => {
    setResultState(processWithGroups(commandList, dataState));
  }, [commandList]);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(commandList, event.dataState);
    setResultState(newDataState);
    setDataState(event.dataState);
  };

  const [isCommandDispatched, setIsCommandDispatched] = React.useState(false);

  const [error, displayError] = React.useState(false);

  const referenceWeightsFilePath: ReferenceWeightsFilePath = useSelector(
    (state: ApplicationState) =>
      state.referenceWeightsReducer.referenceWeightsFilePath,
  );

  const [useCurrentDateTimeFilter, setUseCurrentDateTimeFilter] =
    React.useState<boolean>(true);

  const handleFilterEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUseCurrentDateTimeFilter(event.target.checked);
  };

  useEffect(() => {
    let timeoutId: any;
    if (isCommandDispatched) {
      timeoutId = setTimeout(() => {
        dispatchGetCommandByDateRange();
        setIsCommandDispatched(false);
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isCommandDispatched]);

  const START_DATE_ERRORMSG = GetString('START_DATE_ERRORMSG');
  const END_DATE_ERRORMSG = GetString('END_DATE_ERRORMSG');

  const [startDateRange, setStartDateRange] = React.useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });

  const [endDateRange, setEndDateRange] = React.useState<Date>(new Date());

  const [errorMsg, setErrorMsg] = React.useState(false);

  const [isStartDateRangeChange, setIsStartDateRangeChange] =
    React.useState<boolean>(false);

  const [isEndDateRangeChange, setIsEndDateRangeChange] =
    React.useState<boolean>(false);

  const dispatchGetCommandByDateRange = () => {
    getCommandByDateRange(
      dispatch,
      'ConfigAccessControl',
      startDateRange.toUTCString(),
      useCurrentDateTimeFilter
        ? new Date().toUTCString()
        : endDateRange.toUTCString(),
    );
  };

  useEffect(() => {
    dispatchGetCommandByDateRange();
  }, []);

  useEffect(() => {
    let timeoutId: any;
    if (message !== '') {
      dispatchGetCommandByDateRange();
      timeoutId = setTimeout(() => {
        resetNotificationMessage(dispatch);
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [message]);

  const clearFetchError = () => {
    displayError(false);
    resetCommandFetchError(dispatch);
  };

  useEffect(() => {
    let timeoutId: any;
    if (fetchError.StatusCode > 0 && fetchError.Message != '') {
      displayError(true);
      timeoutId = setTimeout(() => {
        clearFetchError();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchError]);

  const statusCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;

    let status: string = commandStatusTextMap(dataItem.Status);
    let chipStyle: any = useChipStyle(dataItem.Status);
    if (status) {
      return (
        <td>
          <Chip
            className={chipStyle().root}
            text={GetString(status)}
            svgIcon={commandStatusIconMap(dataItem.Status)}
          />
        </td>
      );
    }
    return null;
  };

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <Divider className={classes.divider} />
          <div className={classes.commandHistoryContainer}>
            <StringResourceManager
              stringKey={'ACCESS_CONTROL_HISTORY'}
              variant="body1"
              style="commandLabel"
              gutterBottom={true}
            />
            <div className={classes.startDateRangeContainer}>
              <div className={classes.labelContainer}>
                <StringResourceManager
                  stringKey={'START_DATE_RANGE_LABEL'}
                  variant="body1"
                  style="dialogLabel"
                  gutterBottom={true}
                />
              </div>
              <div className={classes.startDateTimePickerContainer}>
                <div className={classes.dateTimePickerContainer}>
                  <DateTimePicker
                    value={startDateRange}
                    onChange={event => {
                      setStartDateRange(event.value!);
                      setIsStartDateRangeChange(true);
                      setErrorMsg(false);
                    }}
                    width={210}
                  />
                  <Button
                    svgIcon={arrowRotateCwIcon}
                    className={classes.button}
                    onClick={dispatchGetCommandByDateRange}
                  >
                    {GetString('REFRESH')}
                  </Button>
                </div>
                {errorMsg && (
                  <Error direction={'start'}>{START_DATE_ERRORMSG}</Error>
                )}
              </div>
            </div>
            <div className={classes.endDateRangeContainer}>
              <div className={classes.labelContainer}>
                <StringResourceManager
                  stringKey={'END_DATE_RANGE_LABEL'}
                  variant="body1"
                  style="dialogLabel"
                  gutterBottom={true}
                />
              </div>
              <div className={classes.endDateTimePickerContainer}>
                <DateTimePicker
                  value={endDateRange}
                  onChange={event => {
                    setEndDateRange(event.value!);
                    setIsEndDateRangeChange(true);
                    setErrorMsg(false);
                  }}
                  disabled={useCurrentDateTimeFilter}
                  width={210}
                />
                {errorMsg && (
                  <Error direction={'start'}>{END_DATE_ERRORMSG}</Error>
                )}
                &nbsp;&nbsp;
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={handleFilterEndDate}
                      name="checkedB"
                      color="primary"
                      checked={useCurrentDateTimeFilter}
                    />
                  }
                  label={GetString('FILTER_END_DATE_TIME')}
                />
              </div>
            </div>
          </div>

          {loading && LoadingPanel}
          <Grid
            className={classes.gridStyle}
            data={resultState}
            {...dataState}
            resizable={true}
            sortable={true}
            filterable={true}
            pageable={{
              buttonCount: 4,
              pageSizes: [20, 50, 100],
            }}
            reorderable={true}
            fixedScroll={false}
            onDataStateChange={dataStateChange}
            size={'small'}
          >
            <GridColumn
              field="NodeInfo.SiteNumber"
              title={GetString('STORE_TITLE')}
              width="150px"
            />
            <GridColumn
              field="NodeInfo.LaneNumber"
              title={GetString('COLUMN_TITLE_LANE')}
              width="150px"
            />
            <GridColumn
              field="Status"
              title={GetString('STATUS_TITLE')}
              width="200px"
              cells={{
                data: statusCell,
                filterCell: AccessControlHistoryStatusFilterCell,
              }}
            />
            <GridColumn
              field="ExecutionMessage"
              title={GetString('MESSAGE_TITLE')}
              width="220px"
            />
            <GridColumn
              field="CreatedOn"
              title={GetString('CREATED_ON_TITLE')}
              width={'230px'}
              cells={{ data: CreatedOnCell, filterCell: CreatedOnFilterCell }}
            />
            <GridColumn
              field="ExecutionNotifiedOn"
              title={GetString('CLOSE_ON_TITLE')}
              width={'250px'}
              cells={{
                data: CloseOnCell,
                filterCell: ExecutionNotifiedOnFilterCell,
              }}
            />
            <GridColumn
              title={GetString('COLUMN_TITLE_ACTIONS')}
              width="150px"
              filterable={false}
              cells={{ data: actionButton }}
            />
          </Grid>
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {isCommandDispatched && (
                <Notification
                  className={classes.send}
                  type={{
                    style:
                      referenceWeightsFilePath.FilePath !== ''
                        ? 'success'
                        : 'error',
                    icon: true,
                  }}
                  closable={true}
                >
                  {
                    <StringResourceManager
                      stringKey={
                        referenceWeightsFilePath.FilePath !== ''
                          ? 'COMMAND_SENT_SUCCESSFULLY'
                          : 'COMMAND_SEND_FAILED'
                      }
                    />
                  }
                </Notification>
              )}
              {error && (
                <Notification
                  className={classes.send}
                  type={{
                    style: 'error',
                    icon: true,
                  }}
                  closable={true}
                >
                  <StringResourceManager stringKey={fetchError.Message} />
                  {`(${status}: ${fetchError.StatusCode})`}
                </Notification>
              )}
              {message && (
                <Notification
                  className={classes.send}
                  type={{
                    style: 'success',
                    icon: true,
                  }}
                  closable={true}
                >
                  <StringResourceManager stringKey={message} />
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default AccessControlHistoryContainer;
